<template>
    <div class="status-toast" :class="severity">
        <div class="status-indicator mb-4">
            <div class="pulse-dot">
                <svg v-if="severity === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.25001 10L8.75001 12.5L13.75 7.5M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39762 5.39763 1.66666 10 1.66666C14.6024 1.66666 18.3333 5.39762 18.3333 10Z" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_7283_117)">
                        <path d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_7283_117">
                            <rect width="20" height="20" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
            </div>
        </div>

        <div class="text-container">
            <p>{{ title }}</p>
            <span>{{ text }}</span>
        </div>
    </div>
</template>
<script setup lang="ts">
export type ToastSeverity = 'success' | 'error' | 'warning';

withDefaults(defineProps<{
    severity: ToastSeverity;
    title?: string;
    text?: string;
}>(), {
    title: '',
    text: ''
});
</script>
<style scoped>
.status-toast {
    @apply flex gap-3 cursor-pointer;
}
.status-indicator {
    @apply w-5 h-5  items-center justify-center flex;
}
.text-container {
    @apply flex flex-col items-start self-stretch gap-1;

    & > p {
        @apply text-sm font-semibold text-zinc-900;
    }

    & > span {
        @apply text-sm font-normal text-zinc-600;
    }
}

.pulse-dot {
    @apply relative flex items-center justify-center;
}

.pulse-dot :deep(svg),
.pulse-dot :deep(img) {
    @apply w-5 h-5;
}

.pulse-dot::before,
.pulse-dot::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 absolute top-1/2 left-1/2;
}

.status-toast.success svg path {
    @apply stroke-success-600;
}
.status-toast.success .pulse-dot::before,
.status-toast.success .pulse-dot::after {
    @apply border-success-500;
}

.status-toast.error svg path {
    @apply stroke-red-700;
}
.status-toast.error .pulse-dot::before,
.status-toast.error .pulse-dot::after {
    @apply border-red-700;
}

.status-toast.warning svg path {
    @apply stroke-[var(--warning-mark)];
}
.status-toast.warning .pulse-dot::before,
.status-toast.warning .pulse-dot::after {
    @apply border-[var(--warning-mark)];
}

.pulse-dot::before {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-dot::after {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    animation-delay: 0.6s;
}

.pulse-dot:has(svg)::before,
.pulse-dot:has(img)::before,
.pulse-dot:has(svg)::after,
.pulse-dot:has(img)::after {
    animation-name: pulse-ring-with-icon;
}

@keyframes pulse-ring {
    0% {
        @apply w-full h-full opacity-100;
    }
    100% {
        @apply w-[300%] h-[300%] opacity-0;
    }
}

@keyframes pulse-ring-with-icon {
    0% {
        @apply w-full h-full opacity-100;
    }
    100% {
        @apply w-[200%] h-[200%] opacity-0;
    }
}
</style>
